import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import { toggleEmailListItem } from '../../constants/emailListItems';

/**
 * Multi-select modèles d'e-mails — UI uniquement (valeurs inchangées).
 */
const EmailModelsMultiSelect = ({
    id,
    options,
    value = [],
    onChange,
    disabled = false,
    error = null,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const rootRef = useRef(null);
    const searchRef = useRef(null);

    const selected = Array.isArray(value) ? value : [];

    const filteredOptions = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter(
            (opt) =>
                String(opt.label).toLowerCase().includes(q) ||
                String(opt.value).toLowerCase().includes(q)
        );
    }, [options, query]);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setOpen(false);
                setQuery('');
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    useEffect(() => {
        if (open && searchRef.current) {
            searchRef.current.focus();
        }
    }, [open]);

    const allSelected =
        options.length > 0 && options.every((opt) => selected.includes(opt.value));

    const handleToggle = (itemValue) => {
        if (disabled) return;
        onChange(toggleEmailListItem(selected, itemValue));
    };

    const handleSelectAll = () => {
        if (disabled) return;
        onChange(options.map((opt) => opt.value));
    };

    const handleClearAll = () => {
        if (disabled) return;
        onChange([]);
    };

    const summary =
        selected.length === 0
            ? 'Sélectionner des Emails…'
            : `${selected.length} Email${selected.length > 1 ? 's' : ''} sélectionné${selected.length > 1 ? 's' : ''}`;

    return (
        <div className="aee-multiselect">
            <div className="aee-multiselect__control" ref={rootRef}>
                <button
                    type="button"
                    id={id}
                    className={[
                        'aee-multiselect__trigger',
                        open && 'aee-multiselect__trigger--open',
                        error && 'aee-multiselect__trigger--error',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    onClick={() => !disabled && setOpen((prev) => !prev)}
                    disabled={disabled}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-controls={`${id}-listbox`}
                    aria-invalid={Boolean(error)}
                >
                    <span className="aee-multiselect__summary">{summary}</span>
                    <FiChevronDown className="aee-multiselect__chevron" aria-hidden="true" />
                </button>

                {open && (
                    <div className="aee-multiselect__dropdown" role="presentation">
                        <div className="aee-multiselect__search">
                            <FiSearch aria-hidden="true" />
                            <input
                                ref={searchRef}
                                type="search"
                                className="aee-multiselect__search-input"
                                placeholder="Rechercher un Email…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                aria-label="Rechercher un Email"
                                disabled={disabled}
                            />
                        </div>
                        <div className="aee-multiselect__bulk" role="group" aria-label="Actions de sélection">
                            <button
                                type="button"
                                className="aee-multiselect__bulk-btn"
                                onClick={handleSelectAll}
                                disabled={disabled || allSelected}
                            >
                                Tout sélectionner
                            </button>
                            <button
                                type="button"
                                className="aee-multiselect__bulk-btn"
                                onClick={handleClearAll}
                                disabled={disabled || selected.length === 0}
                            >
                                Tout désélectionner
                            </button>
                        </div>
                        <ul
                            id={`${id}-listbox`}
                            className="aee-multiselect__list"
                            role="listbox"
                            aria-multiselectable="true"
                            aria-label="Emails"
                        >
                            {filteredOptions.length === 0 ? (
                                <li className="aee-multiselect__empty">Aucun résultat</li>
                            ) : (
                                filteredOptions.map((opt) => {
                                    const checked = selected.includes(opt.value);
                                    return (
                                        <li key={opt.value} role="option" aria-selected={checked}>
                                            <label className="aee-multiselect__option">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={disabled}
                                                    onChange={() => handleToggle(opt.value)}
                                                />
                                                <span>{opt.label}</span>
                                            </label>
                                        </li>
                                    );
                                })
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmailModelsMultiSelect;
